import React from "react";
import { GitBranch } from "lucide-react";

const SRC = {
    protocol: "border-emerald-400/40 text-emerald-300",
    budget: "border-sky-400/40 text-sky-300",
    contract: "border-violet-400/40 text-violet-300",
};
const STATUS_CHIP = {
    not_found: ["not found", "border-amber-400/60 bg-amber-400/10 text-amber-200"],
    calculated: ["calculated", "border-fuchsia-400/40 text-fuchsia-200"],
    default: ["default", "border-white/20 text-white/60"],
    not_applicable: ["n/a", "border-white/10 text-white/35"],
    site_setting: ["site setting", "border-white/10 text-white/40"],
};
const PCT = ["overhead_percent", "withholding_percent"];
const MONEY = ["initial_amount_per_visit", "flat_sf_amount"];
const GROUPS = [["study_settings", "Study Settings"], ["screen_fail_handling", "Screen Fail Handling"], ["billing_setup", "Billing Setup"]];

const fmtMoney = (v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function settingValue(key, f) {
    if (f.value === null || f.value === undefined) return "—";
    if (typeof f.value === "boolean") return f.value ? "On" : "Off";
    if (PCT.includes(key)) return `${f.value}%`;
    if (MONEY.includes(key)) return fmtMoney(f.value);
    if (key === "net_terms_days") return `${f.value} days`;
    return String(f.value);
}

export function SourceChip({ source, testId }) {
    if (!source) return null;
    return <span data-testid={testId} className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${SRC[source] || "border-white/15 text-white/50"}`}>{source}</span>;
}

/** One setting: label · value · status / source chip, then the quote, calculation or note. */
function SettingRow({ k, f }) {
    const [label, cls] = STATUS_CHIP[f.status] || [];
    const long = typeof f.value === "string" && f.value.length > 60;
    return (
        <div className="py-2 border-t border-white/5 first:border-t-0" data-testid={`sb-setting-${k}`} data-status={f.status}>
            <div className="flex items-baseline gap-2 flex-wrap md:flex-nowrap">
                <span className="text-[11px] text-white/45 w-44 shrink-0">{f.label}</span>
                <span className={`${long ? "text-xs leading-relaxed flex-1 min-w-0" : "text-sm tabular-nums"} ${f.status === "not_found" ? "text-white/30" : "text-white/90"}`} data-testid={`sb-setting-value-${k}`}>{settingValue(k, f)}</span>
                {label ? <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${cls}`} data-testid={`sb-setting-status-${k}`}>{label}</span>
                    : <SourceChip source={f.source} testId={`sb-setting-status-${k}`} />}
                {f.status === "not_found" && f.source && <SourceChip source={f.source} testId={`sb-setting-source-${k}`} />}
            </div>
            {f.quote && <div className="text-[11px] italic text-white/60 mt-0.5 pl-0 md:pl-[11.5rem] whitespace-pre-line" data-testid={`sb-setting-quote-${k}`}>“{f.quote}”</div>}
            {f.calculation && <div className="text-[11px] text-fuchsia-200/80 mt-0.5 md:pl-[11.5rem]" data-testid={`sb-setting-calc-${k}`}>{f.calculation}</div>}
            {f.note && <div className="text-[10px] text-white/40 mt-0.5 md:pl-[11.5rem]">{f.note}</div>}
            {(f.conflicts || []).map((c, i) => (
                <div key={i} className="text-[10px] text-amber-200/80 mt-0.5 md:pl-[11.5rem]" data-testid={`sb-setting-conflict-${k}-${i}`}>
                    {c.source} says {String(c.value)} — “{c.quote}” (lower priority)
                </div>
            ))}
        </div>
    );
}

function Group({ id, title, fields, hideSub }) {
    const entries = Object.entries(fields || {}).filter(([k, f]) => !(hideSub && k !== "screen_fail_handling" && f.status === "not_applicable"));
    const missing = entries.filter(([, f]) => f.status === "not_found").length;
    return (
        <section className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3" data-testid={`sb-setup-group-${id}`}>
            <div className="flex items-center justify-between mb-1">
                <h4 className="text-xs uppercase tracking-wider text-white/60">{title}</h4>
                <span className={`text-[10px] ${missing ? "text-amber-300" : "text-emerald-300"}`} data-testid={`sb-setup-missing-${id}`}>{missing ? `${missing} not found` : "complete"}</span>
            </div>
            {entries.map(([k, f]) => <SettingRow key={k} k={k} f={f} />)}
        </section>
    );
}

function Paths({ paths }) {
    return (
        <section className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3" data-testid="sb-paths">
            <h4 className="text-xs uppercase tracking-wider text-white/60 mb-2">Paths</h4>
            <div className="space-y-2">
                {paths.map((p, i) => (
                    <div key={p.name} className="rounded-lg border border-white/10 px-3 py-2" data-testid={`sb-path-${i}`}>
                        <div className="flex items-center gap-2 text-sm flex-wrap">
                            <span className="text-[10px] text-white/40" data-testid={`sb-path-from-${i}`}>{p.from}</span>
                            <GitBranch className="w-3.5 h-3.5 text-teal-300" />
                            <span className="font-medium text-white/90" data-testid={`sb-path-name-${i}`}>{p.name}</span>
                            <span className="text-[10px] text-white/40">{p.visit_count} visit{p.visit_count === 1 ? "" : "s"}</span>
                            <SourceChip source={p.source} testId={`sb-path-source-${i}`} />
                        </div>
                        {p.note && <div className="text-[11px] text-white/65 mt-1" data-testid={`sb-path-note-${i}`}>{p.note}</div>}
                        {p.quote && <div className="text-[11px] italic text-white/50 mt-0.5">“{p.quote}”</div>}
                        <div className="flex flex-wrap gap-1 mt-1.5">
                            {(p.visits || []).map((v) => <span key={v} className="text-[10px] px-1.5 py-0.5 rounded-full border border-white/10 text-white/55">{v}</span>)}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

/** Study Settings · Screen Fail Handling · Paths · Billing Setup — in SiteCentric's page order. */
export default function StudySetupPanels({ b }) {
    if (!b.study_settings) {
        return <div className="text-sm text-white/50 p-6 text-center" data-testid="sb-setup-empty">This build was made before study settings were added (v2.18) — retry it or start a new build.</div>;
    }
    const sfOff = b.screen_fail_handling?.screen_fail_handling?.value === false;
    const info = b.setup_info || {};
    return (
        <div className="space-y-3" data-testid="sb-setup">
            <div className="text-[11px] text-white/45" data-testid="sb-setup-info">
                Read from {(info.sources_read || []).join(", ") || "—"} · {info.method === "ai" ? "AI + rules" : "rules only"} · protocol wins, then budget, then contract ·
                values no document states are marked <span className="text-amber-300">not found</span>
                {Object.values(info.calls || {}).some((c) => c?.error) && <span className="text-rose-300"> · AI unavailable for part of the setup — rules filled it</span>}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <Group id="study_settings" title={GROUPS[0][1]} fields={b.study_settings} />
                <Group id="screen_fail_handling" title={GROUPS[1][1]} fields={b.screen_fail_handling} hideSub={sfOff} />
            </div>
            <Paths paths={b.paths || []} />
            <Group id="billing_setup" title={GROUPS[2][1]} fields={b.billing_setup} />
        </div>
    );
}
